/**
 * InlineNote — Reusable inline note component with display/edit modes
 * Display mode: text + edit button
 * Edit mode: textarea + Save/Cancel buttons
 * Empty state: "+ Add note" button
 * VA variant: purple accent + meta line (author + date)
 */

import { useEffect, useRef, useState } from 'react'

export interface InlineNoteProps {
  value: string
  onChange: (newValue: string) => void  // called on Save
  placeholder?: string                  // e.g. "Add notes..."
  variant?: 'default' | 'va'           // VA = purple accent
  authorName?: string                   // shown in VA variant
  authorAvatar?: string                 // avatar URL for VA variant
  date?: string                         // date for VA variant meta line
  editable?: boolean                    // default true
  minHeight?: number                    // textarea min-height, default 100
}

export function InlineNote({
  value,
  onChange,
  placeholder = 'Add notes...',
  variant = 'default',
  authorName,
  authorAvatar,
  date,
  editable = true,
  minHeight = 100,
}: InlineNoteProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [draftValue, setDraftValue] = useState(value)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Sync draft with value when not editing
  useEffect(() => {
    if (!isEditing) {
      setDraftValue(value)
    }
  }, [value, isEditing])

  // Auto-focus and resize textarea when entering edit mode
  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus()
      // Reset height and then set to scroll height for auto-resize
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.height = Math.max(minHeight, textareaRef.current.scrollHeight) + 'px'
    }
  }, [isEditing, minHeight])

  const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value
    setDraftValue(newValue)
    // Auto-resize textarea
    e.target.style.height = 'auto'
    e.target.style.height = Math.max(minHeight, e.target.scrollHeight) + 'px'
  }

  const handleSave = () => {
    onChange(draftValue)
    setIsEditing(false)
  }

  const handleCancel = () => {
    setDraftValue(value)
    setIsEditing(false)
  }

  const handleAddNote = () => {
    setDraftValue('')
    setIsEditing(true)
  }

  const isEmpty = !value || value.trim() === ''
  const isVA = variant === 'va'

  // Empty state
  if (isEmpty && !isEditing) {
    return (
      <button
        onClick={handleAddNote}
        disabled={!editable}
        className={`
          w-full px-3 py-3 text-center
          border border-dashed border-[#2a2a44]
          rounded-lg
          text-[#a8a8c0] text-sm
          transition-colors
          ${editable ? 'hover:border-[#3a3a54] hover:text-[#c8c8d8] cursor-pointer' : 'opacity-50 cursor-not-allowed'}
        `}
      >
        + Add note
      </button>
    )
  }

  // Edit mode
  if (isEditing) {
    return (
      <div className="flex flex-col gap-2">
        <textarea
          ref={textareaRef}
          value={draftValue}
          onChange={handleInput}
          placeholder={placeholder}
          className={`
            w-full px-3 py-2
            border border-[#2a2a44]
            rounded-lg
            bg-[#12122a]
            text-[#e8e8f0] text-[13px]
            leading-relaxed
            focus:border-[#3a3a54] focus:ring-1 focus:ring-[#3a3a54]/50
            resize-none
            font-mono
          `}
          style={{ minHeight: `${minHeight}px` }}
        />
        <div className="flex gap-2">
          <button
            onClick={handleSave}
            className={`
              px-4 py-1.5
              bg-[#E61B25] text-white
              rounded-md
              text-[12px] font-medium
              transition-colors
              ${editable ? 'hover:bg-[#d41620]' : 'opacity-50'}
            `}
            disabled={!editable}
          >
            Save
          </button>
          <button
            onClick={handleCancel}
            className={`
              px-4 py-1.5
              border border-transparent
              bg-transparent
              text-[#a8a8c0]
              rounded-md
              text-[12px] font-medium
              transition-colors
              ${editable ? 'hover:text-[#c8c8d8] hover:bg-[#1a1a2e]' : 'opacity-50'}
            `}
            disabled={!editable}
          >
            Cancel
          </button>
        </div>
      </div>
    )
  }

  // Display mode
  return (
    <div className={`relative group flex flex-col gap-2 ${isVA ? 'pl-3 border-l-[3px] border-l-[rgba(139,92,246,0.4)] bg-[rgba(139,92,246,0.05)] py-2' : ''}`}>
      {/* S9-hotfix6 (#4): pencil moved to top-right corner so it doesn't eat a row */}
      {editable && (
        <button
          onClick={() => setIsEditing(true)}
          className="
            absolute top-0 right-0
            p-1
            text-[12px] text-[#8b8ba0]
            opacity-0 group-hover:opacity-100 focus:opacity-100
            transition-opacity
            hover:text-[#e8e8f0]
          "
          aria-label="Edit"
          title="Edit"
        >
          ✎
        </button>
      )}
      <p className="text-[13px] text-[#c8c8d8] leading-relaxed whitespace-pre-wrap pr-6">
        {value}
      </p>

      {/* VA variant meta line */}
      {isVA && authorName && (
        <div className="flex items-center gap-2 mt-2 pt-2 border-t border-[rgba(139,92,246,0.2)]">
          {/* Avatar */}
          {authorAvatar ? (
            <img
              src={authorAvatar}
              alt={authorName}
              className="w-6 h-6 rounded-full object-cover"
            />
          ) : (
            <div className="w-6 h-6 rounded-full bg-[rgba(139,92,246,0.3)] flex items-center justify-center text-[10px] font-medium text-[#a8a8c0]">
              {authorName.charAt(0).toUpperCase()}
            </div>
          )}
          {/* Author name + date */}
          <span className="text-[11px] text-[#a8a8c0]">
            {authorName} {date && `· ${date}`}
          </span>
        </div>
      )}
    </div>
  )
}

export default InlineNote
